import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectValue } from 'antd/lib/select';
import { useHistory } from 'react-router';

import { LLMSettings, ProviderModel, ProviderModelType } from '../../../gamma-api';
import IbSelect from '../common/IbSelect';
import IbButton from '../common/IbButton';
import IbIcon from '../common/IbIcon';
import IbTypography from '../common/IbTypography';
import IbModal from '../common/IbModal';
import { isNumber } from '../../utils/typeUtil';
import IbNumberInput from '../common/IbNumberInput';
import { tryParseFloatValue, tryParseIntValue } from '../../utils/numberUtil';

import { buildModelSelectorOptions, buildModelSelectorValue, parseModelSelectorValue } from './utils';
import {
  MAIN_CLASS_NAME,
  EXTRA_CLASS_NAME,
  EXTRA_ITEM_CLASS_NAME,
  MODAL_CLASS_NAME,
  MODAL_ROW_CLASS_NAME,
  MODAL_ROW_ITEM_CLASS_NAME,
  LLM_SETTINGS,
} from './const';

export interface ILLMModelSelectorProps {
  loading?: boolean;
  settings?: LLMSettings | null;
  providers: ProviderModel[];
  status?: 'default' | 'success' | 'error';
  descriptor?: ReactNode;
  showAdditionalSettings?: boolean;
  onChange: (settings: LLMSettings) => void;
  onBlur?: () => void;
}

const LLMModelSelector: React.FC<ILLMModelSelectorProps> = ({
  loading,
  settings,
  providers,
  status,
  descriptor,
  showAdditionalSettings = true,
  onChange,
  onBlur,
}) => {
  const { t } = useTranslation();
  const { push } = useHistory();

  const [modalVisible, setModalVisible] = useState(false);
  const [modalLLMSettings, setModalLLMSettings] = useState(settings);

  const options = buildModelSelectorOptions(providers, ProviderModelType.Llm);
  const selectorDisabled = !loading && !options.length;

  const modelValue =
    selectorDisabled || loading ? undefined : buildModelSelectorValue(settings?.provider_id, settings?.model_name);

  const selectedModelValue = modelValue && options.some((o) => o.value === modelValue) ? modelValue : undefined;

  const onModelChange = (value: SelectValue) => {
    const [providerId, modelName] = parseModelSelectorValue(value);
    onChange({
      ...settings,
      provider_id: providerId,
      model_name: modelName,
    });
  };

  const onGoToSectionButtonClick = () => push('/providers');

  const onAdditionalSettingsButtonClick = () => {
    setModalLLMSettings(settings);
    setModalVisible(true);
  };

  const onTemperatureChange = (value: string | number) => {
    setModalLLMSettings({
      ...modalLLMSettings,
      temperature: tryParseFloatValue(value),
    });
  };

  const onContextWindowChange = (value: string | number) => {
    setModalLLMSettings({
      ...modalLLMSettings,
      context_window: tryParseIntValue(value),
    });
  };

  const onMaxTokensChange = (value: string | number) => {
    setModalLLMSettings({
      ...modalLLMSettings,
      max_tokens: tryParseIntValue(value),
    });
  };

  const onSeedChange = (value: string | number) => {
    setModalLLMSettings({
      ...modalLLMSettings,
      seed: tryParseIntValue(value),
    });
  };

  const onTopKChange = (value: string | number) => {
    setModalLLMSettings({
      ...modalLLMSettings,
      top_k: tryParseIntValue(value),
    });
  };

  const onTopPChange = (value: string | number) => {
    setModalLLMSettings({
      ...modalLLMSettings,
      top_p: tryParseFloatValue(value),
    });
  };

  const onPresencePenaltyChange = (value: string | number) => {
    setModalLLMSettings({
      ...modalLLMSettings,
      presence_penalty: tryParseFloatValue(value),
    });
  };

  const onModalSaveButtonClick = () => {
    if (modalLLMSettings) {
      onChange(modalLLMSettings);
    }
    setModalVisible(false);
  };

  const onModalCancel = () => setModalVisible(false);

  const modalFooter = (
    <>
      <IbButton onClick={onModalSaveButtonClick}>{t('Model selector: Save')}</IbButton>
      <IbButton type="secondary" onClick={onModalCancel}>
        {t('Model selector: Cancel')}
      </IbButton>
    </>
  );

  return (
    <div className={MAIN_CLASS_NAME}>
      <IbSelect
        disabled={selectorDisabled}
        loading={loading}
        options={options}
        status={status}
        value={selectedModelValue}
        onBlur={onBlur}
        onChange={onModelChange}
      />
      {selectorDisabled ? (
        <IbTypography.Paragraph disabled type="descriptor">
          {t('Model selector: No connected model providers.')}
          <IbButton type="link" onClick={onGoToSectionButtonClick}>
            {t('Model selector: Go to section')}
          </IbButton>
        </IbTypography.Paragraph>
      ) : (
        <>
          {descriptor}
          {showAdditionalSettings && (
            <>
              <IbButton icon={<IbIcon iconName="setting-two" />} type="link" onClick={onAdditionalSettingsButtonClick}>
                {t('Model selector: Additional LLM settings')}
              </IbButton>
              <div className={EXTRA_CLASS_NAME}>
                {isNumber(settings?.temperature) && (
                  <div className={EXTRA_ITEM_CLASS_NAME}>
                    <IbTypography.Paragraph disabled type="secondary">
                      {t('Model selector: Temperature')}
                    </IbTypography.Paragraph>
                    <IbTypography.Paragraph type="secondary">{settings?.temperature}</IbTypography.Paragraph>
                  </div>
                )}
                {isNumber(settings?.context_window) && (
                  <div className={EXTRA_ITEM_CLASS_NAME}>
                    <IbTypography.Paragraph disabled type="secondary">
                      {t('Model selector: Context window')}
                    </IbTypography.Paragraph>
                    <IbTypography.Paragraph type="secondary">{settings?.context_window}</IbTypography.Paragraph>
                  </div>
                )}
                {isNumber(settings?.max_tokens) && (
                  <div className={EXTRA_ITEM_CLASS_NAME}>
                    <IbTypography.Paragraph disabled type="secondary">
                      {t('Model selector: Response size')}
                    </IbTypography.Paragraph>
                    <IbTypography.Paragraph type="secondary">{settings?.max_tokens}</IbTypography.Paragraph>
                  </div>
                )}
                {isNumber(settings?.seed) && (
                  <div className={EXTRA_ITEM_CLASS_NAME}>
                    <IbTypography.Paragraph disabled type="secondary">
                      {t('Model selector: Seed')}
                    </IbTypography.Paragraph>
                    <IbTypography.Paragraph type="secondary">{settings?.seed}</IbTypography.Paragraph>
                  </div>
                )}
                {isNumber(settings?.top_k) && (
                  <div className={EXTRA_ITEM_CLASS_NAME}>
                    <IbTypography.Paragraph disabled type="secondary">
                      {t('Model selector: Top-k')}
                    </IbTypography.Paragraph>
                    <IbTypography.Paragraph type="secondary">{settings?.top_k}</IbTypography.Paragraph>
                  </div>
                )}
                {isNumber(settings?.top_p) && (
                  <div className={EXTRA_ITEM_CLASS_NAME}>
                    <IbTypography.Paragraph disabled type="secondary">
                      {t('Model selector: Top-p')}
                    </IbTypography.Paragraph>
                    <IbTypography.Paragraph type="secondary">{settings?.top_p}</IbTypography.Paragraph>
                  </div>
                )}
                {isNumber(settings?.presence_penalty) && (
                  <div className={EXTRA_ITEM_CLASS_NAME}>
                    <IbTypography.Paragraph disabled type="secondary">
                      {t('Model selector: Presence Penalty')}
                    </IbTypography.Paragraph>
                    <IbTypography.Paragraph type="secondary">{settings?.presence_penalty}</IbTypography.Paragraph>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
      <IbModal
        className={MODAL_CLASS_NAME}
        footer={modalFooter}
        title={t('Model selector modal: Additional LLM settings')}
        visible={modalVisible}
        onCancel={onModalCancel}
      >
        <div className={MODAL_ROW_CLASS_NAME}>
          <div className={MODAL_ROW_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph type="secondary">{t('Model selector: Temperature')}</IbTypography.Paragraph>
            <IbNumberInput
              min={LLM_SETTINGS.temperature.minValue}
              step={LLM_SETTINGS.temperature.step}
              value={modalLLMSettings?.temperature ?? undefined}
              onChange={onTemperatureChange}
            />
          </div>
        </div>
        <div className={MODAL_ROW_CLASS_NAME}>
          <div className={MODAL_ROW_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph type="secondary">{t('Model selector: Max context window')}</IbTypography.Paragraph>
            <IbNumberInput
              min={LLM_SETTINGS.context_window.minValue}
              precision={LLM_SETTINGS.context_window.precision}
              value={modalLLMSettings?.context_window ?? undefined}
              onChange={onContextWindowChange}
            />
          </div>
          <div className={MODAL_ROW_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph type="secondary">{t('Model selector: Max response size')}</IbTypography.Paragraph>
            <IbNumberInput
              min={LLM_SETTINGS.max_tokens.minValue}
              precision={LLM_SETTINGS.max_tokens.precision}
              value={modalLLMSettings?.max_tokens ?? undefined}
              onChange={onMaxTokensChange}
            />
          </div>
        </div>
        <div className={MODAL_ROW_CLASS_NAME}>
          <div className={MODAL_ROW_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph type="secondary">{t('Model selector: Seed')}</IbTypography.Paragraph>
            <IbNumberInput
              precision={LLM_SETTINGS.seed.precision}
              value={modalLLMSettings?.seed ?? undefined}
              onChange={onSeedChange}
            />
          </div>
        </div>
        <div className={MODAL_ROW_CLASS_NAME}>
          <div className={MODAL_ROW_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph type="secondary">{t('Model selector: Top-k')}</IbTypography.Paragraph>
            <IbNumberInput
              min={LLM_SETTINGS.top_k.minValue}
              precision={LLM_SETTINGS.top_k.precision}
              value={modalLLMSettings?.top_k ?? undefined}
              onChange={onTopKChange}
            />
          </div>
          <div className={MODAL_ROW_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph type="secondary">{t('Model selector: Top-p')}</IbTypography.Paragraph>
            <IbNumberInput
              max={LLM_SETTINGS.top_p.maxValue}
              min={LLM_SETTINGS.top_p.minValue}
              step={LLM_SETTINGS.top_p.step}
              value={modalLLMSettings?.top_p ?? undefined}
              onChange={onTopPChange}
            />
          </div>
        </div>
        <div className={MODAL_ROW_CLASS_NAME}>
          <div className={MODAL_ROW_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph type="secondary">{t('Model selector: Presence Penalty')}</IbTypography.Paragraph>
            <IbNumberInput
              step={LLM_SETTINGS.presence_penalty.step}
              value={modalLLMSettings?.presence_penalty ?? undefined}
              onChange={onPresencePenaltyChange}
            />
          </div>
        </div>
      </IbModal>
    </div>
  );
};

export default LLMModelSelector;
